import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Container, Row, Col, Form, Button, Alert, FloatingLabel } from "react-bootstrap"
import { apiClient } from "../services/api/client"
import { initAuthSession } from "../services/auth/session"

const TXT = {
  loginError: "Chyba přihlášení",
  heading: "Přihlášení",
  username: "Uživatelské jméno",
  usernameRequired: "Uveďte uživatelské jméno.",
  password: "Heslo",
  passwordRequired: "Uveďte heslo.",
  hidePassword: "Skrýt heslo",
  showPassword: "Zobrazit heslo",
  forgotPassword: "Zapomněli jste heslo?",
  submit: "Přihlásit",
  appTitle: "Uživatelský přístup",
  appDesc: "Pro přístup zadejte své přihlašovací údaje.",
}

const LoginForm = ({ setUser }) => {
  const navigate = useNavigate()
  const [uzivatel, setUzivatel] = useState("")
  const [heslo, setHeslo] = useState("")
  const [message, setMessage] = useState(null)
  const [validated, setValidated] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setMessage(null)
    }, 3000)

    return () => clearTimeout(timer)
  }, [message])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const form = e.currentTarget

    if (form.checkValidity() === false) {
      e.stopPropagation()
      setValidated(true)
      return
    }

    setValidated(true)

    try {
      const data = await apiClient.post("/login", { uzivatel, heslo })

      initAuthSession()

      sessionStorage.removeItem("reservationsVisibleDayCount")
      sessionStorage.removeItem("mechanicOrderStatusFilter")
      sessionStorage.setItem("user", JSON.stringify(data.user))
      setUser(data.user)

      setTimeout(() => {
        navigate("/dashboard")
      }, 100)
    } catch (error) {
      setMessage(error.message || TXT.loginError)
    }
  }

  return (
    <Container fluid className="vh-100 d-flex align-items-center justify-content-center bg-primary px-5">
      <div className="login-container shadow-lg rounded-4 overflow-hidden mx-sm-5">
        <Row className="g-0">
          <Col md={6} className="p-5 bg-white order-2 order-md-1">
            <Form noValidate validated={validated} onSubmit={handleSubmit} className="h-100 d-flex flex-column justify-content-center">
              <h3 className="mb-4">{TXT.heading}</h3>

              <Form.Group className="mb-2" controlId="formUzivatel">
                <FloatingLabel controlId="uzivatel" label={TXT.username}>
                  <Form.Control
                    className="rounded-pill"
                    type="text"
                    placeholder={TXT.username}
                    value={uzivatel}
                    maxLength={32}
                    onChange={(e) => setUzivatel(e.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">{TXT.usernameRequired}</Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>

              <Form.Group className="mb-1" controlId="formHeslo">
                <div className="input-group has-validation password-input-group mb-3">
                  <FloatingLabel controlId="heslo" label={TXT.password} className="flex-grow-1">
                    <Form.Control
                      className="rounded-start-pill"
                      type={showPassword ? "text" : "password"}
                      placeholder={TXT.password}
                      value={heslo}
                      maxLength={64}
                      onChange={(e) => setHeslo(e.target.value)}
                      required
                    />
                  </FloatingLabel>
                  <button
                    type="button"
                    className="btn btn-outline-secondary password-toggle-group-btn rounded-end-pill"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? TXT.hidePassword : TXT.showPassword}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                  </button>
                  <div className="invalid-feedback">{TXT.passwordRequired}</div>
                </div>
              </Form.Group>

              <div className="mb-4">
                <Link to="/pw-reset" className="text-muted small">{TXT.forgotPassword}</Link>
              </div>

              <Button type="submit" size="lg" className="w-100 rounded-pill py-2">
                {TXT.submit}
              </Button>

              {message && (
                <Alert variant="danger" className="mt-3 rounded-pill py-2">
                  <i className="bi bi-exclamation-triangle pe-2"></i>
                  {message}
                </Alert>
              )}
            </Form>
          </Col>

          <Col md={6} className="bg-primary text-white d-flex flex-column justify-content-center align-items-center p-4 position-relative order-1 order-md-2">
            <svg
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{ opacity: 0.1, zIndex: 0 }}
              viewBox="1700 0 17000 17000"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8469.39 4560.02v3344.05l1836.83-1081.97 1836.83 1081.97V4560.02l-1836.83-1081.96-1836.83 1081.96zm1836.83 9195.12h1836.83v-500.87l-1836.83-1081.97-1836.83 1081.97v500.87h1836.83zm1836.83-4848v3344.05l-1836.83-1081.96-1836.83 1081.96V8907.14l1836.83-1081.96 1836.83 1081.96zM7617.87 8405.66V5061.62L5202.73 6484.23l-.03 3344.04zM7617.87 12752.78V9408.74l-2415.14 1422.61v1767.97c0 513.88 339.93 952.19 805.98 1101.32l1609.16-947.86zM6358.55 3478.06h1259.32v580.48L5202.73 5481.12v-847.24c0-635.7 520.12-1155.82 1155.82-1155.82zM12994.57 8405.66V5061.62l2415.14 1422.61.03 3344.04zM12994.57 12752.78V9408.74l2415.14 1422.61v1767.97c0 513.88-339.93 952.19-805.98 1101.32l-1609.16-947.86zM14253.89 3478.06h-1259.32v580.48l2415.14 1422.58v-847.24c0-635.7-520.12-1155.82-1155.82-1155.82z"/>
            </svg>
            <div style={{ zIndex: 1 }} className="text-center">
              <i className="bi bi-journal-text display-3"></i>
              <h3 className="mt-3">{TXT.appTitle}</h3>
              <p>{TXT.appDesc}</p>
            </div>
          </Col>
        </Row>
      </div>
    </Container>
  )
}

export default LoginForm



